import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { Link } from 'react-router-dom'
import { useNavigate, useParams } from 'react-router-dom'
import type { ArtistId } from 'types/artist'
import type { ClientLibraryArtist, ClientLibraryId } from 'types/client-library'
import type { Location } from 'react-router-dom'
import type { State } from 'types/redux'
import { clientLibraryArtistLoad } from '@disco/state-management/actions/client-library-artist-actions'
import { getClientLibraryArtist } from '@disco/state-management/selectors/client-libraries'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import { TabNav } from 'components/ui'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import Skeleton from 'components/ui/skeleton'
type Props = {
  children: React.ReactNode
  location: Location
}

const LibrariesLibraryArtistsArtist = ({
  children,
  location,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { libraryId, artistId } =
    useParams<{ libraryId: string; artistId: string }>()
  const libraryIdNum: ClientLibraryId = parseInt(libraryId!, 10)
  const artistIdNum: ArtistId = parseInt(artistId!, 10)
  const artist: ClientLibraryArtist | null | undefined = useSelector(
    (state: State) => getClientLibraryArtist(state, libraryIdNum, artistIdNum),
  )
  const artistLinkedTo: ClientLibraryArtist | null | undefined = useSelector(
    (state: State) =>
      artist?.related_to
        ? getClientLibraryArtist(state, libraryIdNum, artist.related_to)
        : null,
  )
  const typeData = useComposerInsteadOfArtist().value
  useEffect(() => {
    dispatch(clientLibraryArtistLoad(libraryIdNum, artistIdNum))
  }, [])

  const handleCancel = () => {
    const returnTo = `/catalogs/${libraryIdNum}/artists`
    navigate(location.state?.returnTo ?? returnTo)
  }

  const NAV_ITEMS = useMemo(() => {
    const base = `/catalogs/${libraryIdNum}/artists`
    let items = [
      {
        label: 'Profile',
        link: {
          pathname: `${base}/${artistIdNum}/profile`,
          state: location.state,
        },
      },
      {
        label: 'Featured tracks',
        link: {
          pathname: `${base}/${artistIdNum}/featured-tracks`,
          state: location.state,
        },
      },
      {
        label:
          artist && artistLinkedTo ? (
            <>
              <span
                style={{
                  opacity: 0.5,
                }}
              >
                Linked {typeData}s
              </span>
              <HelpTooltip>
                {artist.name} is already linked to{' '}
                <Link to={`${base}/${artistLinkedTo.id}/linked-artists`}>
                  {artistLinkedTo.name}
                </Link>
              </HelpTooltip>
            </>
          ) : (
            <>Linked {typeData}s</>
          ),
        link: artistLinkedTo
          ? null
          : {
              pathname: `${base}/${artistIdNum}/linked-artists`,
              state: location.state,
            },
      },
    ]
    return items
  }, [libraryIdNum, artistIdNum, artistLinkedTo?.id])
  return (
    <ModalWindow
      width="medium"
      header={
        <Heading size={700}>
          {artist && !artist.loading ? (
            `Manage ${typeData} – ${artist.name}`
          ) : (
            <Skeleton />
          )}
        </Heading>
      }
      isOpen={true}
      onClose={handleCancel}
    >
      <DocumentHeadTags title={`Manage ${typeData} | DISCO`} />
      <TabNav className="Tabs--modal" items={NAV_ITEMS} />
      {children}
    </ModalWindow>
  )
}

export default LibrariesLibraryArtistsArtist
